/* agmf-footer-peek.js — the footer strip is a handle (Dorian 2026-09-02).
 *
 * On the desktop product page the layout leaves a strip of the footer visible
 * under the two panes. Scrolling on it reveals the rest, but nothing SAID so:
 * a strip of black with a row of headings reads as a footer that has been
 * cut off, not as something you can open. So:
 *
 *   - a small chevron sits centred at the top edge of the strip, pointing up
 *     while the footer is tucked and down once it is open;
 *   - a click ANYWHERE on the strip while it is tucked opens the footer;
 *   - once open, its links work, and a click on the footer's TOP BAND (or the
 *     chevron) folds it back.
 *
 * Own file, nothing shared: it reads one class the desktop layout sets on
 * <html> (`agdesk-on`) and touches nothing but its own button. Off desktop it
 * never mounts, and the stylesheet hides the chevron outright.
 */
(function () {
  'use strict';
  var root = document.documentElement;
  var section = null, inner = null, cue = null;

  function desktop() { return root.classList.contains('agdesk-on'); }

  // ── ONE STATE, SET BY US (Dorian 2026-09-02: folding/unfolding "is a bit
  // glitchy"). The state used to be DERIVED from geometry on every scroll
  // event - including the frames of its own smooth scroll. So a click turned
  // the chevron, the animation then crossed the threshold and turned it
  // again, and a second click read a half-finished scroll and did the
  // opposite of what it looked like. Now a click sets the state and paints
  // once; geometry only re-syncs when the shopper scrolls by themselves,
  // never while our own scroll is in flight.
  var state = 'tucked';                  // 'tucked' | 'open'
  var animUntil = 0, lastToggle = 0;
  var TOP_BAND = 64;                     // the fold-back band at the footer's top

  function now() { return Date.now(); }

  // ── THE HANDLE BAND TRACKS THE STRIP, NOT A MAGIC 160.
  // "Tucked" used to mean "the footer's top is in the bottom 160px", which is
  // 4.4x the 36px strip - so between 36 and 160px of visible footer there was
  // a 124px band where the footer stood plainly open and every click in it was
  // still swallowed and turned back into "open the footer". Reproduced in a
  // harness on this very file: at 136px showing, the "Returns" link is fully
  // on screen, the chevron still says tucked, and clicking it re-opens instead
  // of navigating; one pixel, 159 to 160, separated dead from alive. A hand-
  // wheeled footer lands in that band on the first notch.
  //
  // So the band is measured against the strip itself (--agdesk-footer-peek),
  // with a little hysteresis so a pixel of jitter cannot flap it: a strip plus
  // a few pixels is still a handle; anything meaningfully open is a footer,
  // and its links are links.
  function peekPx() {
    var v = getComputedStyle(root).getPropertyValue('--agdesk-footer-peek');
    return parseFloat(v) || 36;
  }
  function showingPx() {
    return Math.max(0, window.innerHeight - section.getBoundingClientRect().top);
  }
  function geomTucked() {
    if (!section) return false;
    var s = showingPx(), p = peekPx();
    return state === 'open' ? s < p + 10 : s < p + 24;
  }

  function paint() {
    if (!cue) return;
    var t = state === 'tucked';
    cue.classList.toggle('is-tucked', t);
    inner.classList.toggle('agmf-foot-tucked', t);
    cue.setAttribute('aria-label', t ? 'Show the footer' : 'Back to the product');
  }

  function sync() {
    if (!section || now() < animUntil) return;
    var t = geomTucked() ? 'tucked' : 'open';
    if (t === state) return;
    state = t;
    paint();
  }

  function glide(y) {
    animUntil = now() + 900;
    window.scrollTo({ top: Math.max(0, Math.round(y)), behavior: 'smooth' });
    paint();                             // the chevron turns now, not mid-flight
  }

  // ── WHAT WILL COVER THE FOOTER'S TOP once the scroll lands - asked as a
  // HEIGHT, never as a position (Dorian 2026-09-07: "sometimes the bar is
  // opening too much and then its okay again").
  //
  // MEASURED CAUSE. This used to read the header's live
  // `getBoundingClientRect().bottom`. The header auto-hides by sliding
  // (agmf-desktop-header.js sets html.agmf-hdr-hidden, and the stylesheet
  // moves it with `transform: translateY(-143px)`) - so that rect reports
  // ~143 while the header shows, ~0 once it is away, and anything between
  // during the third of a second it takes to travel. Click the strip with
  // the header hidden and the footer was told to scroll 143px further than
  // it should: it opened too far. Click it with the header showing and the
  // same code was right. That is exactly "too much, and then okay again".
  //
  // A transform does not change a HEIGHT, so offsetHeight is the same number
  // whatever the slide is doing. And only what STICKS can cover anything: the
  // announcement bar scrolls away with the page (it is counted only where the
  // theme makes it sticky), and a header that is hidden covers nothing at all.
  function coveredPx() {
    var api = window.AGMFDesktopHeader;
    if (api && typeof api.isHidden === 'function' && api.isHidden()) return 0;
    var px = 0;
    var hdr = document.querySelector('header.shopify-section--header');
    if (hdr && getComputedStyle(hdr).position === 'sticky') px += hdr.offsetHeight;
    var bar = document.querySelector('[id$="__announcement-bar"]');
    if (bar && getComputedStyle(bar).position === 'sticky') px += bar.offsetHeight;
    return Math.max(0, Math.round(px));
  }

  // Open = bring the footer's TOP into view, not the document's end. On a big
  // screen those are the same scroll. On a short one the footer is taller
  // than the room left, and scrolling to the end showed only its bottom rows.
  function openFooter() {
    var top = section.getBoundingClientRect().top + window.scrollY;
    var covered = coveredPx();
    var end = document.scrollingElement.scrollHeight - window.innerHeight;
    state = 'open';
    glide(Math.min(end, top - covered));
  }
  function closeFooter() { state = 'tucked'; glide(0); }

  function toggle() {
    if (now() - lastToggle < 350) return;   // a double click is one decision
    lastToggle = now();
    if (state === 'tucked') openFooter(); else closeFooter();
  }

  function mount() {
    if (cue || !desktop()) return;
    section = document.querySelector('footer.shopify-section');
    inner = section && section.querySelector('.footer');
    if (!inner) return;
    cue = document.createElement('button');
    cue.type = 'button';
    cue.className = 'agmf-foot-cue';
    cue.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true">' +
      '<path d="M6 14.5l6-6 6 6" fill="none" stroke="currentColor" stroke-width="2.4" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
    inner.appendChild(cue);
    state = geomTucked() ? 'tucked' : 'open';
    paint();
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
  }

  document.addEventListener('click', function (e) {
    if (!desktop() || !section || !e.target.closest) return;
    if (e.target.closest('.agmf-foot-cue')) { e.preventDefault(); toggle(); return; }
    if (!section.contains(e.target)) return;
    if (state === 'tucked') {
      // the strip is a handle, and a heading in it is part of the handle
      e.preventDefault();
      toggle();
      return;
    }
    // OPEN: a click on the footer's top band folds it back (Dorian
    // 2026-09-02, "clicking anywhere on the top of the footer should fold it
    // back") - but a link in that band is still a link.
    if (e.target.closest('a, button, input, select, textarea, label, summary, [role="button"]')) return;
    var y = e.clientY - section.getBoundingClientRect().top;
    if (y >= 0 && y <= TOP_BAND) { e.preventDefault(); toggle(); }
  }, true);

  new MutationObserver(mount).observe(root, { attributes: true, attributeFilter: ['class'] });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount);
  else mount();
}());
